<div class="agent-invoices-container">
  <!-- Header -->
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Agent Invoices</h1>
        <p>Generate and manage invoices for agents based on their transactions</p>
      </div>
      <button class="generate-btn" (click)="openGenerateModal()">
        <i class="fas fa-plus-circle"></i> Generate New Invoice
      </button>
    </div>
  </div>

  <!-- Search/Filters -->
  <div class="filters-section">
    <div class="search-box">
      <i class="fas fa-search search-icon"></i>
      <input type="text" [(ngModel)]="searchText" (keyup.enter)="onSearch()" placeholder="Search by agent name..." class="search-input">
      <button class="search-btn" (click)="onSearch()" [disabled]="loading">Search</button>
      <button class="clear-btn" (click)="clearSearch()" *ngIf="searchText">
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>

  <!-- Success Message -->
  <div *ngIf="successMessage" class="success-message">
    <i class="fas fa-check-circle mr-2"></i>
    {{ successMessage }}
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <i class="fas fa-exclamation-circle mr-2"></i>
    {{ errorMessage }}
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading-state">
    <div class="loading-spinner"></div>
    <span class="loading-text">Loading invoices...</span>
  </div>

  <!-- Desktop Table View -->
  <div *ngIf="!loading && invoices.length > 0" class="desktop-table-container">
    <div class="table-wrapper">
      <table class="invoices-table">
        <thead>
          <tr>
            <th>Invoice ID</th>
            <th>Agent Name</th>
            <th>Invoice Date</th>
            <th>Transactions</th>
            <th>Total Amount</th>
            <th>Paid Amount</th>
            <th>Balance Due</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let invoice of invoices">
            <tr class="invoice-row" [class.is-expanded]="isExpanded(invoice.invoiceId)">
              <td class="invoice-id">{{ invoice.invoiceId }}</td>
              <td class="agent-name">{{ invoice.agentName || 'Unknown Agent' }}</td>
              <td class="date">{{ formatDate(invoice.invoiceDate) }}</td>
              <td class="tx-count text-center">{{ invoice.transactionCount }}</td>
              <td class="amount">{{ formatCurrency(invoice.totalAmount) }}</td>
              <td class="amount" style="color: #059669;">{{ formatCurrency(invoice.paidAmount || 0) }}</td>
              <td class="amount" style="color: #dc2626;">{{ formatCurrency(invoice.balanceDue !== undefined ? invoice.balanceDue : Math.max(0, invoice.totalAmount - (invoice.paidAmount || 0))) }}</td>
              <td class="status">
                <span class="status-badge"
                  [class.status-paid]="invoice.status === 'Paid'"
                  [class.status-partially-paid]="invoice.status === 'PartiallyPaid' || invoice.status === 'PartialPaid'"
                  [class.status-pending]="invoice.status === 'Pending'">
                  {{ invoice.status === 'PartiallyPaid' ? 'Partially Paid' : invoice.status }}
                </span>
              </td>
              <td class="actions">
                <div class="actions-group">
                  <button class="view-tx-btn" (click)="toggleExpandInvoice(invoice.invoiceId)"
                    [class.active]="isExpanded(invoice.invoiceId)"
                    title="View Charge Summary for this Invoice">
                    <i class="fas" [class.fa-chevron-down]="!isExpanded(invoice.invoiceId)" [class.fa-chevron-up]="isExpanded(invoice.invoiceId)"></i>
                    <span>{{ isExpanded(invoice.invoiceId) ? 'Hide' : 'Summary' }}</span>
                  </button>
                  <button class="download-btn" (click)="downloadInvoice(invoice)" title="Download PDF" [disabled]="!invoice.invoiceDownloadUrl">
                    <i class="fas fa-file-pdf"></i> Download
                  </button>
                </div>
              </td>
            </tr>
            <!-- Inline Expandable Row for Invoice Charge Summary -->
            <tr *ngIf="isExpanded(invoice.invoiceId)" class="expanded-transactions-row">
              <td colspan="9" class="expanded-transactions-cell">
                <div class="transactions-expansion-container">
                  <div class="expansion-header">
                    <span class="expansion-title">
                      <i class="fas fa-file-invoice-dollar"></i> Charge Summary in Invoice <strong>{{ invoice.invoiceId }}</strong>
                    </span>
                    <span class="expansion-count-badge">
                      {{ invoice.transactions?.length || 0 }} {{ (invoice.transactions?.length === 1) ? 'Transaction' : 'Transactions' }}
                    </span>
                  </div>

                  <div *ngIf="!invoice.transactions || invoice.transactions.length === 0" class="no-tx-message">
                    <i class="fas fa-info-circle mr-2"></i> No transaction details available for this invoice.
                  </div>

                  <div *ngIf="invoice.transactions && invoice.transactions.length > 0" class="tx-table-wrapper charge-summary-wrapper">
                    <table class="inline-tx-table charge-summary-table">
                      <thead>
                        <tr>
                          <th>Charge Type</th>
                          <th class="text-right">Count</th>
                          <th class="text-right">Taxable (₹)</th>
                          <th class="text-right">CGST (₹)</th>
                          <th class="text-right">SGST (₹)</th>
                          <th class="text-right">Total (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr *ngFor="let item of getChargeSummary(invoice)">
                          <td class="charge-type">{{ item.type }}</td>
                          <td class="count text-right">{{ item.count }}</td>
                          <td class="amount text-right">{{ formatCurrency(item.taxableAmount) }}</td>
                          <td class="amount text-right">{{ formatCurrency(item.cgst) }}</td>
                          <td class="amount text-right">{{ formatCurrency(item.sgst) }}</td>
                          <td class="amount text-right total">{{ formatCurrency(item.totalAmount) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="!loading && invoices.length > 0" class="pagination-container">
    <app-pagination [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
      (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- No Data State -->
  <div *ngIf="!loading && invoices.length === 0" class="no-data-state">
    <div class="no-data-content">
      <i class="fas fa-file-invoice-dollar"></i>
      <h3>No Invoices Found</h3>
      <p *ngIf="searchText">No invoices match your search for "{{ searchText }}".</p>
      <p *ngIf="!searchText">No agent invoices have been generated yet.</p>
      <button class="generate-btn mt-4" (click)="openGenerateModal()" *ngIf="!searchText">
        Generate Your First Invoice
      </button>
    </div>
  </div>

  <!-- Generate Invoice Modal -->
  <div *ngIf="showGenerateModal" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h2>Generate Agent Invoice</h2>
        <button class="modal-close-btn" (click)="closeGenerateModal()">&times;</button>
      </div>
      <div class="modal-body">
        <p class="modal-instruction">Select an agent to generate an invoice for their pending transactions.</p>
        
        <div class="form-group">
          <label for="agentSelect">Select Agent:</label>
          <select id="agentSelect" [(ngModel)]="selectedAgentId" (change)="onAgentSelected()" class="form-select" [disabled]="generating">
            <option value="" disabled selected>-- Select an Agent --</option>
            <option *ngFor="let agent of agents" [value]="agent.agentId">
              {{ agent.name }} ({{ agent.phoneNumber || 'No phone' }})
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="toDateInput">To Date (Optional):</label>
          <input type="date" id="toDateInput" [(ngModel)]="selectedToDate" (change)="onAgentSelected()" class="form-input" [disabled]="generating">
          <small class="form-text">All transactions created up to and including the end of this day will be considered.</small>
        </div>

        <!-- Transactions Preview -->
        <div *ngIf="loadingTransactions" class="modal-loading">
          <div class="loading-spinner-small"></div>
          <span>Fetching pending transactions...</span>
        </div>

        <div *ngIf="!loadingTransactions && selectedAgentId && uninvoicedTransactions.length > 0" class="transactions-preview">
          <div class="preview-header">
            <h3>Pending Transactions ({{ uninvoicedTransactions.length }})</h3>
            <span class="total-badge">Total: {{ formatCurrency(totalPendingAmount) }}</span>
          </div>
          <div class="transactions-list">
            <table class="preview-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th class="text-right">Taxable Amount</th>
                  <th class="text-right">CGST</th>
                  <th class="text-right">SGST</th>
                  <th class="text-right">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let tx of uninvoicedTransactions">
                  <td>{{ formatDate(tx.createdAt) }}</td>
                  <td>{{ tx.type }}</td>
                  <td class="amount text-right">{{ formatCurrency(tx.amount) }}</td>
                  <td class="amount text-right">{{ formatCurrency(tx.cgst) }}</td>
                  <td class="amount text-right">{{ formatCurrency(tx.sgst) }}</td>
                  <td class="amount text-right">{{ formatCurrency(tx.totalAmount) }}</td>
                </tr>
              </tbody>
              <tfoot class="preview-footer">
                <tr>
                  <td colspan="2" class="text-right font-bold">Summary:</td>
                  <td class="amount text-right font-bold">{{ formatCurrency(calculateSubtotal()) }}</td>
                  <td class="amount text-right font-bold">{{ formatCurrency(calculateTotalCgst()) }}</td>
                  <td class="amount text-right font-bold">{{ formatCurrency(calculateTotalSgst()) }}</td>
                  <td class="amount text-right font-bold">{{ formatCurrency(roundOffAmount > 0 ? calculateRawTotal() : totalPendingAmount) }}</td>
                </tr>
                <tr *ngIf="roundOffAmount > 0">
                  <td colspan="5" class="text-right font-bold" style="color: #6b7280;">Round Off:</td>
                  <td class="amount text-right font-bold" style="color: #6b7280;">+{{ formatCurrency(roundOffAmount) }}</td>
                </tr>
                <tr *ngIf="roundOffAmount > 0">
                  <td colspan="5" class="text-right font-bold" style="color: #1e3a8a;">Total Amount (Rounded):</td>
                  <td class="amount text-right font-bold" style="color: #1e3a8a;">{{ formatCurrency(totalPendingAmount) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div *ngIf="!loadingTransactions && selectedAgentId && uninvoicedTransactions.length === 0 && !errorMessage" class="no-transactions-warning">
          <i class="fas fa-info-circle"></i>
          This agent has no pending transactions to invoice.
        </div>

        <div *ngIf="errorMessage" class="modal-error">
          <i class="fas fa-exclamation-triangle mr-2"></i> {{ errorMessage }}
        </div>

        <div class="modal-actions">
          <button class="cancel-btn" (click)="closeGenerateModal()" [disabled]="generating">Cancel</button>
          <button class="confirm-btn" (click)="generateInvoice()" [disabled]="!selectedAgentId || generating || uninvoicedTransactions.length === 0">
            <span *ngIf="!generating"><i class="fas fa-file-invoice"></i> Generate Invoice</span>
            <span *ngIf="generating"><i class="fas fa-spinner fa-spin"></i> Generating...</span>
          </button>
        </div>
      </div>
    </div>
  </div>

</div>
